<template>
  <div class="ds-control ds-table-layout" :class="getCss">
    <div class="ds-table-layout-box" :style="tableBorderStyle">
      <div ref="observer"></div>
      <table
        ref="table"
        class="ds-table-layout-table"
        v-resize.x.y="{
          colList: 'columnsInfo',
          rowList: 'rows',
          onChangeCol: onChangeCol,
          onChangeRow: onChangeRow,
        }">
        <thead>
          <tr>
            <th v-for="(col, $index) in columnsInfo" :key="$index" ref="th"></th>
          </tr>
        </thead>
        <tbody>
          <slot name="prepend"></slot>
          <slot name="default"></slot>
          <slot name="append"></slot>
        </tbody>
      </table>
      <div v-if="isDesign" v-show="rangeWrap.show" :style="selectedRangeStyle" class="selected-range"></div>
    </div>
  </div>
</template>
<script>
import directives from "./tableResize";
import DsfTableRow from "./tableRow";
import DsfTableCell from "./tableCell";

const minCellWidth = 50;

/**
 * @class DsfTable
 * @augments Control
 * @description 表格布局
 * @example
 * <DsfTable></DsfTable>
 */
export default dsf.component({
  name: "DsfTable",
  ctrlCaption: "表格布局",
  mixins: [$mixins.control],
  provide() {
    return {
      $tableLayout: this
    };
  },
  components: {
    DsfTableRow,
    DsfTableCell
  },
  directives,
  props: {
    // 复制粘贴进来的html片段
    clipboardString: {
      type: String,
      default: ""
    },
    slots: {
      type: Array,
      default() {
        return [
          {
            name: "default",
            controls: []
          }
        ];
      }
    },
    // 列宽设置
    columnsInfo: {
      type: Array,
      default() {
        return [];
      }
    },
    rowsInfo: {
      type: Array,
      default() {
        return [];
      }
    },
    showBorder: {
      type: Boolean,
      default: true
    },
    noInsideBorder: {
      type: Boolean,
      default: false
    },
    fixedLayout: {
      type: Boolean,
      default: true
    },
    isBlockControl: {
      type: Boolean,
      default: true
    },
    borderWidth: {
      type: Number,
      default: 1
    },
    borderStyle: {
      type: String,
      default: "solid"
    },
    borderColor: {
      type: String,
      default: ""
    },
    outBorderShowSite: {
      // 外边框显示的位置
      type: Array,
      default: () => {
        return ["top", "right", "bottom", "left"];
      }
    }
  },
  data() {
    this.__resizeObserver = null;
    this.__realColumnsInfo = [];
    return {
      rangeWrap: {
        show: false,
        startRowIndex: 0,
        endRowIndex: 0,
        startColIndex: 0,
        endColIndex: 0,
        width: 0,
        height: 0,
        top: 0,
        left: 0,
        selected: []
      },
      allCells: {},
      matrix: [],
      // isDown: false,
      downCell: null,
      cellNums: 0,
      tableRows: [],
      cellZIndex: 1,
      designInitRowCount: 4,
      designInitColumnCount: 4
    };
  },
  computed: {
    rows() {
      return this.getRows();
    },
    getCss() {
      return [
        ...this.$getClass(),
        // this.showBorder ? "" : "ds-no-border",
        this.borderStyle == "none" ? "ds-no-border" : "",
        // !this.visible ? 'ds-hide' : '',
        !this.noPadding ? "" : "ds-no-padding",
        this.fixedLayout ? "ds-table-fixed" : "",
        ...this.classList
      ];
    },
    outBorderWidth() {
      if (this.borderStyle == "double") {
        return this.unitSize(this.borderWidth);
      } else {
        return 0;
      }
    },
    tableBorderStyle() {
      // 外边框样式集合
      let style = {};
      if (this.borderStyle == "double") {
        let padding = this.noInsideBorder ? this.isDesign ? '1px' : '0px' : this.outBorderWidth;
        if (this.outBorderShowSite.length === 4) {
          style['border'] = `${this.outBorderWidth} solid ${this.borderColor}`;
          style['padding'] = padding;
        } else {
          _.forEach(this.outBorderShowSite, (site) => {
            style[`border-${site}`] = `${this.outBorderWidth} solid ${this.borderColor}`;
            style[`padding-${site}`] = padding;
          });
        }
      }
      return style;
    },
    selectedRangeStyle() {
      // 设计器选中时的框样式
      let rangeWrap = this.rangeWrap;
      return {
        height: rangeWrap.height + "px",
        width: rangeWrap.width + "px",
        top: rangeWrap.top + "px",
        left: rangeWrap.left + "px"
      };
    },
  },
  watch: {
    columnsInfo: {
      handler() {
        this.updateRealColumnsInfo();
      },
      deep: true
    },
    borderWidth() {
      this.updateRealColumnsInfo();
    }
  },
  created() {
    if (this.isDesign && this.slots[0].controls.length === 0) {
      // 如果是粘贴进来的table
      if (this.clipboardString) {
        fromClipboardString.call(this);
      } else {
        // 设计模式下 默认情况
        let colCount = this.designInitColumnCount;
        for (let i = 0; i < colCount; i++) {
          addColumn.call(this);
        }
        let rowCount = this.designInitRowCount;
        //设计模式下默认生成4行
        for (let i = 0; i < rowCount; i++) {
          let row = addRow.call(this);
          for (let n = 0; n < colCount; n++) {
            addCell.call(this, row);
          }
        }
      }
    }
  },
  mounted() {
    if (this.isDesign) {
      parseMatrix.call(this);
    }
    this.__resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) {
        let {contentRect: {width: elWidth}} = entry;
        this.updateRealColumnsInfo(_.round(elWidth));
      }
    });
    this.__resizeObserver.observe(this.$refs.observer);
  },
  updated() {
    if (this.isDesign) {
      updateRangeWrap.call(this, this.rangeWrap.selected);
    }
  },
  beforeDestroy() {
    if (this.__resizeObserver) {
      this.__resizeObserver.disconnect();
    }
  },
  methods: {
    updateRealColumnsInfo(elWidth) {
      this.$nextTick(() => {
        if (elWidth === void 0) {
          if (!this.$refs.table) return;
          elWidth = this.$refs.observer.offsetWidth;
        }
        elWidth -= this.borderWidth;
        let allWidth = 0, scaleMap = {}, autoMap = {}, concreteMap = {},
          minWidth = minCellWidth, averageWidth = elWidth / this.columnsInfo.length;
        if (averageWidth < minWidth) {
          minWidth = averageWidth;
        }
        _.eachRight(this.columnsInfo, ({width}, i) => {
          let w = parseFloat(width);
          if (!width || isNaN(w)) {
            autoMap[i] = 'auto';
          } else if (/%$/.test(width)) {
            scaleMap[i] = w;
            allWidth += w * elWidth / 100;
          } else {
            concreteMap[i] = w;
            allWidth += w;
          }
        });
        let autoLength = _.values(autoMap).length;
        if (elWidth - allWidth < autoLength * minWidth) {
          let equivalentWidth = elWidth - (autoLength * minWidth);
          _.each(concreteMap, (w, i) => {
            concreteMap[i] = equivalentWidth * w / allWidth;
          });
          _.each(scaleMap, (w, i) => {
            let OldWidth = w * elWidth / 100;
            concreteMap[i] = equivalentWidth * OldWidth / allWidth;
          });
          _.each(autoMap, (w, i) => {
            concreteMap[i] = minWidth;
          });
        } else {
          _.each(scaleMap, (w, i) => {
            concreteMap[i] = w * elWidth / 100;
          });
          let equivalentWidth = _.reduce(concreteMap, (s, n) => s + n, 0);
          let autoWidth = (elWidth - equivalentWidth) / autoLength;
          _.each(autoMap, (w, i) => {
            concreteMap[i] = autoWidth;
          });
        }
        let equivalentWidth = 0;
        _.each(concreteMap, (w, i) => {
          equivalentWidth += concreteMap[i] = _.round(w);
        });
        let offWidth = elWidth - equivalentWidth, u = offWidth > 0 ? 1 : -1;
        if (offWidth != 0) {
          let l = this.columnsInfo.length, i = l;
          while (offWidth) {
            offWidth -= u;
            concreteMap[--i] += u;
            if (i == 0) i = l;
          }
        }
        _.each(concreteMap, (w, i) => {
          if (this.__realColumnsInfo[i] !== w) {
            this.$refs.th[i].style.width = w + 'px';
            this.__realColumnsInfo[i] = w;
          }
        });
        if (this.isDesign) {
          updateRangeWrap.call(this, this.rangeWrap.selected);
        }
      });
    },
    updateRangeWrap() {
      updateRangeWrap.call(this, this.rangeWrap.selected);
    },
    setSelectedCellsByPosition,
    getRows() {
      if (this.isDesign) {
        _.each(this.slots[0].controls, (row, i) => {
          row.rowIndex = i;
        });
        return this.slots[0].controls;
      } else {
        return this.tableRows;
      }
    },
    getCells(row) {
      return row.slots[0].controls;
    },
    getCellWidth(cell, index) {
      let width;
      if (cell) {
        if (cell.colspan === 1) {
          width = this.columnsInfo[index].width;
        }
      } else {
        width = this.columnsInfo[index].width;
      }
      return width || "auto";
    },
    isSelected(rindex, cindex) {
      let result = _.find(this.rangeWrap.selected, (c) => {
        return rindex === c.rowIndex && cindex === c.cellIndex;
      });
      return !!result;
    },
    // 列宽被改变
    onChangeCol(colList) {
      this.$emit("update:columnsInfo", colList);
      if (this.isDesign) {
        this.$nextTick(() => {
          updateRangeWrap.call(this, this.rangeWrap.selected);
        });
      }
    },
    // 行高被改变
    onChangeRow(index, height) {
      this.rows[index].height = height;
      if (this.isDesign) {
        this.$nextTick(() => {
          updateRangeWrap.call(this, this.rangeWrap.selected);
        });
      }
    },
    $childComponentAddToCell(cell, ctrl, slot) {},
    // 设置单元格样式
    setCellStyle({attr, value}) {
      let m = parseMatrix.call(this);
      let { maxRowIndex, minRowIndex, maxCellIndex, minCellIndex } = getRangeSelectedRect.call(this);
      let fn = (value) => {
        // 处理选中区域中间单元格
        for (let i = minRowIndex; i <= maxRowIndex; i++) {
          for (let j = minCellIndex; j <= maxCellIndex; j++) {
            const vm = m[i][j];
            if (!vm.isSpan) {
              if (attr === 'align' || attr === 'valign') {
                this.$set(vm.cellAttr, attr, value);
              } else {
                this.$set(vm.cellStyle, attr, value);
              }
            }
          }
        }
      }
      if ((attr === 'color' || attr === 'background-color') && value === 'custom') {
        this.setCellCustomColor(m[minRowIndex][minCellIndex].cellStyle[attr] || '#ffffff', fn);
      } else fn(value);
    },
    setCellBorder({attr, value}) {
      let m = parseMatrix.call(this);
      let { maxRowIndex, minRowIndex, maxCellIndex, minCellIndex } = getRangeSelectedRect.call(this);
      let [position, att = ''] = attr.split(",");
      let fnTop = (value, breakFor = false) => {
        if (!breakFor) for (let j = minCellIndex; j <= maxCellIndex; j++) {
          fn2(m[minRowIndex][j], 'top', att, value);
        }
        minRowIndex > 0 && _.each(m[minRowIndex - 1], (vm, i) => {
          if (i <= maxCellIndex && (vm.colspan + 1) + i - 1 >= minCellIndex) {
            let n = minRowIndex - 2;
            while (vm.isSpan && n >= 0) {
              vm = m[n--][i];
            }
            fn2(vm, 'bottom', 'none', 'none');
          }
        });
      };
      let fnBottom = (value, breakFor = false) => {
        if (!breakFor) for (let j = minCellIndex; j <= maxCellIndex; j++) {
          let i = maxRowIndex;
          while (m[i][j].isSpan && i > minRowIndex) {
            i--;
          }
          fn2(m[i][j], 'bottom', att, value);
        }
        att == 'none' && maxRowIndex + 1 < m.length && _.each(m[maxRowIndex + 1], (vm, i) => {
          if (i <= maxCellIndex && (vm.colspan + 1) + i - 1 >= minCellIndex) {
            let n = maxRowIndex + 2;
            while (vm.isSpan && n < m.length) {
              vm = m[n++][i];
            }
            fn2(vm, 'top', 'none', 'none');
          }
        });
      };
      let fnLeft = (value, breakFor = false) => {
        if (!breakFor) for (let i = minRowIndex; i <= maxRowIndex; i++) {
          fn2(m[i][minCellIndex], 'left', att, value);
        }
        minCellIndex > 0 && _.each(m, (arr, i) => {
          let vm = arr[minCellIndex - 1];
          if (i <= maxRowIndex && (vm.rowspan || 1) + i - 1 >= minRowIndex) {
            let n = minCellIndex - 2;
            while (vm.isSpan && n > 0) {
              vm = arr[n--];
            }
            fn2(vm, 'right', 'none', 'none');
          }
        });
      };
      let fnRight = (value, breakFor = false) => {
        if (!breakFor) for (let i = minRowIndex; i <= maxRowIndex; i++) {
          let j = maxCellIndex;
          while (m[i][j].isSpan && j > minCellIndex) {
            j--;
          }
          fn2(m[i][j], 'right', att, value);
        }
        att == 'none' && maxCellIndex + 1 < m[minRowIndex].length && _.each(m, (arr, i) => {
          let vm = arr[maxCellIndex + 1];
          if (i <= maxRowIndex && (vm.rowspan || 1) + i - 1 >= minRowIndex) {
            let n = maxCellIndex + 2;
            while (vm.isSpan && n < m[minRowIndex].length) {
              vm = arr[n++];
            }
            fn2(vm, 'left', 'none', 'none');
          }
        });
      };
      let fn1 = (value) => {
        switch (position) {
        case 'all':
          for (let i = minRowIndex; i <= maxRowIndex; i++) {
            for (let j = minCellIndex; j <= maxCellIndex; j++) {
              const vm = m[i][j];
              fn2(vm, 'top', att, value);
              fn2(vm, 'bottom', att, value);
              fn2(vm, 'left', att, value);
              fn2(vm, 'right', att, value);
            }
          }
          fnTop(value, true);
          fnBottom(value, true);
          fnLeft(value, true);
          fnRight(value, true);
          break;
        case 'top':
          fnTop(value);
          break;
        case 'bottom':
          fnBottom(value);
          break;
        case 'left':
          fnLeft(value);
          break;
        case 'right':
          fnRight(value);
          break;
        }
      };
      let fn2 = (vm, position, attr, value) => {
        if (vm.isSpan) return;
        switch (attr) {
        case '':
          this.$set(vm, 'cellBorderStyle', {});
          break;
        case 'none':
          this.$set(vm.cellBorderStyle, `border-${position}-style`, value);
          break;
        case 'style':
          this.$set(vm.cellBorderStyle, `border-${position}-style`, value);
          break;
        case 'color':
          if (vm.cellBorderStyle[`border-${position}-style`] === 'none') {
            this.$set(vm.cellBorderStyle, `border-${position}-style`, 'solid');
          }
          this.$set(vm.cellBorderStyle, `border-${position}-color`, value);
          break;
        }
      };
      if (attr.indexOf('color') > -1 && value === 'custom') {
        this.setCellCustomColor('#666666', fn1);
      } else fn1(value);
    },
    setCellCustomColor(value, cb) {
      dsf.layer.openDialog({
        title: '选择颜色',
        content: "DsfColorSelect",
        width: "400px",
        params: {
          value
        },
        btns: [
          {
            text: "确定",
            handler: (res) => {
              cb(res.valueCopy);
            }
          },
          {
            text: "取消"
          }
        ]
      })
    },
  },
  design: {
    isMask: false,
    //右键菜单处理
    contextMenuHandler(evt) {
      let target = evt.target;
      target = $(evt.target).closest("TD");
      target = filterSelfCell.call(this, target);
      if (target && target.length && target.get(0).tagName === "TD") {
        target = $(target);
        if (!target.attr("c-index")) return [];
        let pos = target.attr("c-index").split(",");
        let rowIndex = Number(pos[0]);
        let cellIndex = Number(pos[1]);
        //如果右键的单元格不再已经框选的范围内则重新以右键的单元格为当前选中单元格
        let result = _.findIndex(this.rangeWrap.selected, (c) => {
          return c.rowIndex === rowIndex && c.cellIndex === cellIndex;
        });
        if (result < 0) {
          this.downCell = target;
          this.rangeWrap.show = true;
          getRangeSelectedCells.call(this, this.downCell, null);
        }
        let selectedCells = this.rangeWrap.selected;
        let { maxRowIndex, minRowIndex, maxCellIndex, minCellIndex } = getRangeSelectedRect.call(this);
        let selectedRange = (maxRowIndex - minRowIndex + 1) * (maxCellIndex - minCellIndex + 1);
        let { setCellStyle, setCellBorder } = this;
        let menus = [
          { text: "合并单元格", id: 'mergeCells', disabled: selectedRange < 2 || selectedCells.length == 1, handler: mergeCellsHanlder },
          { text: "拆分单元格", id: 'splitCell', disabled: selectedRange < 2 || selectedCells.length == selectedRange, handler: splitCellHandler },
          "-",
          { text: "插入行", children: [
            { text: "插入行(前)", id: 'insertRowBefore', disabled: false, handler: insertRowBefore },
            { text: "插入行(后)", id: 'insertRowAfter', disabled: false, handler: insertRowAfter }
          ]},
          { text: "插入列", children: [
            { text: "插入列(前)", id: 'insertColumnBefore', disabled: false, handler: insertColumnBefore },
            { text: "插入列(后)", id: 'insertColumnAfter', disabled: false, handler: insertColumnAfter }
          ]},
          "-",
          { text: "删除", children: [
            { text: "删除整行", id: 'deleteRows', disabled: false, handler: deleteRows },
            { text: "删除整列", id: 'deleteColumns', disabled: false, handler: deleteColumns },
          ]},
          "-",
          { text: "重置列宽", id: 'resetColWidth', disabled: false, handler: resetColWidth },
          { text: "重置行高", id: 'resetRowHeight', disabled: false, handler: resetRowHeight },
          "-",
          { text: "单元格设置", id: 'cellStyle', disabled: false, children: getCellContextMenuConfig(setCellStyle, setCellBorder) },
          { text: "清除选中格式", id: 'clearChooseCell', disabled: false, handler: clearChooseCell },
          { text: "清除所有格式", id: 'clearAllCell', disabled: false, handler: clearAllCell }
        ];
        // 执行右键菜单的拦截器
        this.contextMenuInterceptors?.(menus);
        return menus;
      }
    },
    //清空右键菜单弹出过的痕迹
    // clearContextMemuHandler() {
    //   this.isDown = false;
    //   clearRangeWrap.call(this);
    // },
    //设计器全局的mousedown事件，设计器会向下级所有组件触发,可用于清理当前触发过mousedown事件留下的痕迹
    clearMouseDownHandler() {
      this.isDown = false;
      clearRangeWrap.call(this);
    },
    //鼠标down事件处理
    mouseDownHandler(evt, designer, target) {
      if (this.isDesign && evt.button === 0) {
        this.isDown = true;
        this.downCell = $(target);
        this.rangeWrap.show = true;
        parseMatrix.call(this);
        getRangeSelectedCells.call(this, this.downCell, null);
        evt.stopPropagation();
      }
    },
    //鼠标移动事件处理
    mouseMoveHandler(evt) {
      if (this.isDown) {
        let overCell = $(evt.target).closest("TD");
        let target = null;
        do {
          target = filterSelfCell.call(this, overCell);
          if (!target) {
            overCell = overCell.parents("TD");
            if (overCell.length === 0) {
              return;
            }
          }
        } while (!target);
        if (overCell) {
          getRangeSelectedCells.call(this, this.downCell, overCell);
          evt.stopPropagation();
        }
      }
    },
    //鼠标up事件处理
    mouseUpHandler() {
      this.isDown = false;
      parseMatrix.call(this);
    }
  }
});

function fromClipboardString() {
  let clipboardTable = $(this.clipboardString).appendTo("body");
  const trs = clipboardTable.find("tr");
  let columns = [];
  for (let i = 0; i < trs.length; i++) {
    let tr = $(trs[i]);
    const tds = tr.find("td");
    let columnNum = 0;
    _.each(tds, (td) => {
      td = $(td);
      const colspan = ~~td.attr("colspan") || 1;
      columnNum += colspan;
      if (colspan <= 1);
      {
        columns[columnNum - 1] = {
          width: td.innerWidth()
        };
      }
    });
    let row = addRow.call(this);
    for (let j = 0; j < tds.length; j++) {
      const td = $(tds[j]);
      if (td) {
        const rowspan = ~~td.attr("rowspan") || 1;
        const colspan = ~~td.attr("colspan") || 1;
        const text = td.html();
        addCell.call(this, row, { rowspan, colspan, text });
      }
    }
  }
  let sum = 0;
  _.each(columns, (it) => {
    sum += ~~it.width;
  });
  for (let c = 0; c < columns.length; c++) {
    let w = (~~columns[c].width / sum) * 100;
    addColumn.call(this, null, w + "%");
  }
  clipboardTable.remove();
}

function addRow() {
  let row = createRow();
  this.slots[0].controls.push(row);
  return row;
}
function addCell(row, slots) {
  let cell = createCell(slots);
  row.slots[0].controls.push(cell);
  return cell;
}
function addColumn(index, width = "auto") {
  if (dsf.isUnDef(index) || index < 0) {
    this.columnsInfo.push({ width: width });
  } else {
    this.columnsInfo.splice(index, 0, { width: width });
  }
}
function removeColumn(index) {
  dsf.array.removeAt(this.columnsInfo, index);
}
//获取被框选的单元格
function getRangeSelectedCells(start, end) {
  let selected = [];
  if (end && start.get(0) != end.get(0)) {
    if (!start.attr("c-index") || !end.attr("c-index")) {
      return;
    }
    let pos_s = start.attr("c-index").split(",");
    let ri_s = Number(pos_s[0]);
    let ci_s = Number(pos_s[1]);
    let pos_e = end.attr("c-index").split(",");
    let ri_e = Number(pos_e[0]);
    let ci_e = Number(pos_e[1]);
    return setSelectedCellsByPosition.call(this, ci_s, ri_s, ci_e, ri_e);
  } else {
    if (!start.attr("c-index")) {
      return;
    }
    let pos_s = start.attr("c-index").split(",");
    let rowIndex = Number(pos_s[0]);
    let cellIndex = Number(pos_s[1]);
    let cell = getCellByIndex.call(this, rowIndex, cellIndex);
    if (cell) {
      dsf.array.ensure(selected, cell);
    }
    return setSelectedCellsByPosition.call(this, cellIndex, rowIndex, cellIndex, rowIndex);
  }
}

// 根据坐标框选单元格
function setSelectedCellsByPosition(x1, y1, x2, y2) {
  let c1 = Math.min(x1, x2);
  let c2 = Math.max(x1, x2);
  let r1 = Math.min(y1, y2);
  let r2 = Math.max(y1, y2);
  while (true) {
    let c11 = c1, c22 = c2, r11 = r1, r22 = r2;
    for (let i = r11, l = r22; i <= l; i++) {
      let row = this.matrix[i];
      for (let j = c11, m = c22; j <= m; j++) {
        let cell = row[j];
        if (cell.isSpan) {
          let {rowIndex, cellIndex, colspan, rowspan} = cell.mergeCell;
          r11 = Math.min(r11, rowIndex);
          r22 = Math.max(r22, rowIndex + rowspan - 1);
          c11 = Math.min(c11, cellIndex);
          c22 = Math.max(c22, cellIndex + colspan - 1);
        } else if (cell.colspan > 1 || cell.rowspan > 1) {
          let {rowIndex, cellIndex, colspan, rowspan} = cell;
          r11 = Math.min(r11, rowIndex);
          r22 = Math.max(r22, rowIndex + rowspan - 1);
          c11 = Math.min(c11, cellIndex);
          c22 = Math.max(c22, cellIndex + colspan - 1);
        }
      }
    }
    if (c11 == c1 && c22 == c2 && r11 == r1 && r22 == r2) break;
    c1 = c11, c2 = c22, r1 = r11, r2 = r22;
  }
  let selected = [];
  for (let i = r1; i <= r2; i++) {
    let row = this.matrix[i];
    for (let j = c1; j <= c2; j++) {
      let cell = row[j];
      if (!cell.isSpan) {
        selected.push(cell);
      }
    }
  }
  updateRangeWrap.call(this, selected);
  return selected;
}

/*获取框选矩形范围 */
function getRangeSelectedRect() {
  let {startRowIndex, endRowIndex, startColIndex, endColIndex} = this.rangeWrap;
  return {
    maxRowIndex: endRowIndex,
    minRowIndex: startRowIndex,
    maxCellIndex: endColIndex,
    minCellIndex: startColIndex
  };
}

//创建列
function createCell(slots = {}) {
  return dsf.designer.createComponent(DsfTableCell, slots);
}
//创建行
function createRow() {
  return dsf.designer.createComponent(DsfTableRow);
}
//计算合并单元格
function mergeCellsHanlder() {
  let cells = this.rangeWrap.selected;
  let range = getRangeSelectedRect.call(this);
  let r = cells[0].rowIndex;
  let c = cells[0].cellIndex;
  let rows = this.getCells(this.rows[r]);

  let cell = _.find(rows, (ce) => ce.cellIndex === c);
  mergeCells.call(this, cell, range.maxRowIndex - range.minRowIndex + 1, range.maxCellIndex - range.minCellIndex + 1);
}

//拆分单元格
function splitCellHandler() {
  let cells = getMergedCells.call(this);
  splitCell.call(this, cells);
}

//获取被合并的单元格
function getMergedCells() {
  let selected = this.rangeWrap.selected;
  let cells = [];
  for (let i = 0; i < selected.length; i++) {
    let c = selected[i];
    if (c.rowspan > 1 || c.colspan > 1) {
      cells.push(c);
    }
  }
  return cells;
}

//将表格转换为矩阵数组
function parseMatrix() {
  let matrix = []; //let offsetArray = [];
  let rows = this.getRows();
  if (rows && rows.length) {
    let allCells = {};
    _.each(rows, () => {
      matrix.push([]);
      // offsetArray.push(0);
    });
    for (let i = 0; i < rows.length; i++) {
      let row = rows[i];
      let cells = this.getCells(row);
      let offset_cell = 0;
      for (let n = 0; n < cells.length; n++) {
        let cell = cells[n];
        cell.rowIndex = i;
        cell.cellIndex = n;
        let rs = cell.rowspan || 1;
        let cs = cell.colspan || 1;
        while (matrix[i][offset_cell] !== undefined) {
          offset_cell++;
        }
        for (let r = i; r < i + rs; r++) {
          for (let c = offset_cell; c < offset_cell + cs; c++) {
            if (r === i && c === offset_cell) {
              matrix[r][c] = cell;
              cell.rowIndex = i;
              cell.cellIndex = c;
              allCells[cell.rowIndex + "," + cell.cellIndex] = cell;
            } else {
              matrix[r][c] = {
                isSpan: true,
                rowIndex: i,
                cellIndex: offset_cell,
                mergeCell: cell
              };
            }
          }
        }
      }
    }
    this.allCells = allCells;
  }
  this.matrix = matrix;
  let columnLength = this.matrix[0] ? this.matrix[0].length : 0;
  _.eachRight(this.columnsInfo, (c, i) => {
    if (i > columnLength - 1) {
      dsf.array.remove(this.columnsInfo, c);
    }
  });
  return matrix;
}

//通过单元格获取索引
function getIndexByCellProp(prop) {
  for (let k in this.allCells) {
    if (this.allCells[k] === prop) {
      let pos = k.split(",");
      return {
        rowIndex: Number(pos[0]),
        cellIndex: Number(pos[1])
      };
    }
  }
  return null;
}

//通过索引号获取单元格对象
function getCellByIndex(rowIndex, cellIndex) {
  return this.allCells[rowIndex + "," + cellIndex];
}

//通过矩阵数组更新表格数据
function updateRowsAndCells(m) {
  // let arr = [];
  for (let i = 0; i < m.length; i++) {
    let row = m[i];
    let arrs = [];
    for (let c = 0; c < row.length; c++) {
      let cell = row[c];
      if (cell && !cell.isSpan) {
        arrs.push(cell);
      }
    }
    this.rows[i].slots[0].controls = arrs;
  }
  parseMatrix.call(this);
  this?.$designer?.recordPageChange?.();
}

//更新选中区域
function updateRangeWrap(cells) {
  if (!this.isDesign) {
    return;
  }
  if (!cells) {
    return;
  }
  let top = Number.MAX_SAFE_INTEGER,
    left = Number.MAX_SAFE_INTEGER,
    right = Number.MIN_SAFE_INTEGER,
    bottom = Number.MIN_SAFE_INTEGER,
    startRowIndex = Number.MAX_SAFE_INTEGER,
    endRowIndex = Number.MIN_SAFE_INTEGER,
    startColIndex = Number.MAX_SAFE_INTEGER,
    endColIndex = Number.MIN_SAFE_INTEGER;
  let fn = ({rowIndex, cellIndex, colspan, rowspan}) => {
    let cell = $(this.$el).find("td[c-index='" + rowIndex + "," + cellIndex + "']");
    startRowIndex = Math.min(startRowIndex, rowIndex);
    endRowIndex = Math.max(endRowIndex, rowIndex + rowspan - 1);
    startColIndex = Math.min(startColIndex, cellIndex);
    endColIndex = Math.max(endColIndex, cellIndex + colspan - 1);
    cell = filterSelfCell.call(this, cell);
    if (cell) {
      let cellOffset = cell.eq(0).position();
      let cellParent = cell.parent();
      let _top = cellParent.position().top;
      let _left = cellOffset.left;
      top = Math.min(top, _top);
      left = Math.min(left, _left);
      right = Math.max(right, _left + cell.outerWidth());
      bottom = Math.max(bottom, _top + cell.outerHeight());
    }
  };
  _.each(cells, (cell) => cell.isSpan || fn(cell));
  let width = right - left;
  let height = bottom - top;
  let borderWidth = this.borderWidth;
  this.rangeWrap.startRowIndex = startRowIndex;
  this.rangeWrap.endRowIndex = endRowIndex;
  this.rangeWrap.startColIndex = startColIndex;
  this.rangeWrap.endColIndex = endColIndex;
  let offw = borderWidth % 2;
  this.rangeWrap.width = width + 4 - offw;
  this.rangeWrap.height = height + 4 - offw;
  this.rangeWrap.left = left - 2 + this.$refs.table.offsetLeft;
  this.rangeWrap.top = top - 2 + this.$refs.table.offsetTop + offw;
  this.rangeWrap.selected = cells;
}

//清空框选
function clearRangeWrap() {
  if (this.rangeWrap.show) {
    this.rangeWrap.show = false;
    this.rangeWrap.startRowIndex = 0;
    this.rangeWrap.endRowIndex = 0;
    this.rangeWrap.startColIndex = 0;
    this.rangeWrap.endColIndex = 0;
    this.rangeWrap.width = 0;
    this.rangeWrap.height = 0;
    this.rangeWrap.left = -10;
    this.rangeWrap.top = -10;
    this.rangeWrap.selected = [];
  }
}

//过滤自身的td
function filterSelfCell(cells) {
  for (let i = 0; i < cells.length; i++) {
    let cell = cells.eq(i);
    let table = cell.closest("table");
    if (this.$refs.table === table.get(0)) {
      return cell;
    }
  }
  return null;
}

//合并单元格
function mergeCells(cell, rowSpan, cellSpan) {
  rowSpan = rowSpan || 1;
  cellSpan = cellSpan || 1;
  let pos = getIndexByCellProp.call(this, cell);
  let m = parseMatrix.call(this);
  for (let i = pos.rowIndex; i < pos.rowIndex + rowSpan; i++) {
    for (let n = pos.cellIndex; n < pos.cellIndex + cellSpan; n++) {
      if (i === pos.rowIndex && n === pos.cellIndex) {
        m[i].splice(n, 1, cell);
        cell.rowspan = rowSpan;
        cell.colspan = cellSpan;
      } else {
        m[i].splice(n, 1, {
          isSpan: true,
          rowIndex: cell.rowIndex,
          cellIndex: cell.cellIndex,
          mergeCell: cell
        });
      }
    }
  }
  updateRowsAndCells.call(this, m);
  if (this.rangeWrap.show) {
    this.rangeWrap.selected = [this.rangeWrap.selected[0]];
  }
}

//拆分单元格
function splitCell(cells) {
  let { maxRowIndex, minRowIndex, maxCellIndex, minCellIndex } = getRangeSelectedRect.call(this);
  parseMatrix.call(this);
  _.each(cells, (c) => {
    for (let i = c.rowIndex; i < c.rowIndex + c.rowspan; i++) {
      for (let n = c.cellIndex; n < c.cellIndex + c.colspan; n++) {
        let cell = this.matrix[i][n];
        if (cell.isSpan) {
          this.matrix[i][n] = createCell();
        }
      }
    }
    c.rowspan = 1;
    c.colspan = 1;
  });
  updateRowsAndCells.call(this, this.matrix);
  if (this.rangeWrap.show) {
    let res = [];
    for (let i = minCellIndex; i <= maxCellIndex; i++) {
      for (let j = minRowIndex; j <= maxRowIndex; j++) {
        res.push(getCellByIndex.call(this, j, i));
      }
    }
    this.rangeWrap.selected = res;
  }
}

//插入行（前）
function insertRowBefore() {
  parseMatrix.call(this);
  let _temp = [];
  let range = getRangeSelectedRect.call(this);
  let currIndex = range.minRowIndex;
  let insertRow = this.matrix[currIndex];
  let row = createRow();
  for (let i = 0; i < insertRow.length; i++) {
    let cell = insertRow[i];
    if (cell.isSpan) {
      cell = this.matrix[cell.rowIndex][cell.cellIndex];
    }
    if (cell.rowspan > 1 && currIndex > cell.rowIndex) {
      dsf.array.ensure(_temp, cell);
      continue;
    }
    let newCell = createCell();
    row.slots[0].controls.push(newCell);
  }
  _.each(_temp, (cell) => {
    cell.rowspan++;
  });
  this.rows.splice(currIndex, 0, row);
  parseMatrix.call(this);
}
//插入行（后）
function insertRowAfter() {
  parseMatrix.call(this);
  let _temp = [];
  let range = getRangeSelectedRect.call(this);
  let currIndex = range.maxRowIndex;
  let insertRow = this.matrix[currIndex];
  let row = createRow();
  for (let i = 0; i < insertRow.length; i++) {
    let cell = insertRow[i];
    if (cell.isSpan) {
      cell = this.matrix[cell.rowIndex][cell.cellIndex];
    }
    if (cell.rowspan > 1 && currIndex < cell.rowIndex + cell.rowspan - 1) {
      dsf.array.ensure(_temp, cell);
      continue;
    }
    let newCell = createCell();
    row.slots[0].controls.push(newCell);
  }
  _.each(_temp, (cell) => {
    cell.rowspan++;
  });
  this.rows.splice(currIndex + 1, 0, row);
  parseMatrix.call(this);
}

//插入列（前）
function insertColumnBefore() {
  parseMatrix.call(this);
  let range = getRangeSelectedRect.call(this);
  let {rows, matrix} = this, currIndex = range.minCellIndex;
  if (currIndex == 0) {
    _.each(rows, row => {
      row.slots[0].controls.unshift(createCell());
    });
  } else {
    _.each(rows, (row, rIndex) => {
      let mRow = matrix[rIndex];
      let cell = mRow[currIndex];
      if (cell.isSpan && (mRow[currIndex - 1].isSpan || mRow[currIndex - 1] === cell.mergeCell)) {
        cell.mergeCell.colspan++;
      } else {
        let controls = row.slots[0].controls;
        let index = _.findIndex(controls, c => {
          return c.cellIndex == currIndex
        });
        controls.splice(index, 0, createCell());
      }
    });
  }
  addColumn.call(this, currIndex);
  parseMatrix.call(this);
}

//插入列（后）
function insertColumnAfter() {
  parseMatrix.call(this);
  let range = getRangeSelectedRect.call(this);
  let {rows, matrix} = this, currIndex = range.maxCellIndex;
  if (currIndex == matrix[0].length - 1) {
    _.each(rows, row => {
      row.slots[0].controls.push(createCell());
    });
  } else {
    _.each(rows, (row, rIndex) => {
      let mRow = matrix[rIndex];
      let cell = mRow[currIndex];
      if (cell.colspan > 1) {
        cell.colspan++;
      } else if (cell.isSpan && mRow[currIndex + 1].isSpan) {
        cell.mergeCell.colspan++;
      } else {
        let controls = row.slots[0].controls;
        let index = _.findIndex(controls, c => {
          return c.cellIndex > currIndex;
        })
        controls.splice(index, 0, createCell());
      }
    });
  }
  addColumn.call(this, currIndex + 1);
  parseMatrix.call(this);
}

//删除行
function deleteRows() {
  parseMatrix.call(this);
  let range = getRangeSelectedRect.call(this);
  // let currIndex = range.minRowIndex;
  for (let i = range.maxRowIndex; i >= range.minRowIndex; i--) {
    const row = this.matrix[i];
    let n = row.length - 1;
    while (n >= 0) {
      let cell = row[n];
      if (cell.isSpan) {
        cell = this.matrix[cell.rowIndex][cell.cellIndex];
      }
      if (cell.rowspan > 1) {
        if (i > cell.rowIndex) {
          cell.rowspan--;
          n -= cell.colspan;
        } else {
          //补齐单元格
          if (cell.rowIndex === i && cell.cellIndex === n) {
            let newCell = createCell();
            newCell.rowspan = cell.rowspan - 1 > 1 ? cell.rowspan - 1 : 1;
            newCell.colspan = cell.colspan;
            let pos = _.findIndex(this.rows[i + 1].slots[0].controls, (c) => {
              return c.cellIndex > cell.cellIndex;
            });
            if (pos >= 0) {
              this.rows[i + 1].slots[0].controls.splice(pos, 0, newCell);
            } else {
              this.rows[i + 1].slots[0].controls.push(newCell);
            }
          }
          n--;
        }
      } else {
        n--;
      }
    }
    this.rows.splice(i, 1);
  }
  parseMatrix.call(this);
}
//删除列
function deleteColumns() {
  parseMatrix.call(this);
  let range = getRangeSelectedRect.call(this);
  for (let n = range.maxCellIndex; n >= range.minCellIndex; n--) {
    let i = this.matrix.length - 1;
    while (i >= 0) {
      let row = this.matrix[i];
      let cells = this.rows[i].slots[0].controls;
      let cell = row[n];
      if (cell.isSpan) {
        cell = this.matrix[cell.rowIndex][cell.cellIndex];
      }
      if (cell.colspan > 1) {
        if (n > cell.cellIndex) {
          cell.colspan--;
          i -= cell.rowspan;
        } else {
          //删除了colspan>1的列，需要在填补一个空位
          if (cell.rowIndex === i && cell.cellIndex === n) {
            let newCell = createCell();
            newCell.colspan = cell.colspan - 1 > 1 ? cell.colspan - 1 : 1;
            newCell.rowspan = cell.rowspan;
            let index = _.findIndex(cells, (cc) => cc === cell);
            cells.splice(index, 1, newCell);
          }
          i--;
        }
      } else {
        if (i === cell.rowIndex) {
          dsf.array.remove(cells, cell);
        }
        i--;
      }
    }
    removeColumn.call(this, n);
  }
  parseMatrix.call(this);
}

// 重置列宽
function resetColWidth() {
  const newWidth = "auto";
  this.onChangeCol(
    this.columnsInfo.map(() => {
      return { width: newWidth };
    })
  );
}
// 重置行高
function resetRowHeight() {
  this.rows.forEach((vm) => {
    vm.height = "57px";
  });
}

// 单元格格式
function setTableCell() {
  dsf.layer.openDialog({
    title: "单元格设置",
    fullscreen: false,
    content: "DesTableCellSet",
    width: "600px",
    btns: [
      {
        text: "确定",
        handler: (vm) => {
          const cellStyle = vm.yes();
          setTableCellStyle.call(this, cellStyle);
          this.$forceUpdate();
        }
      },
      {
        text: "取消"
      }
    ]
  });
}
function setTableCellStyle({ cellStyle, cellAttr, borderPosition }) {
  let m = parseMatrix.call(this);
  let { maxRowIndex, minRowIndex, maxCellIndex, minCellIndex } = getRangeSelectedRect.call(this);
  // 处理选中区域中间单元格
  let border;
  for (let i = minRowIndex; i <= maxRowIndex; i++) {
    for (let j = minCellIndex; j <= maxCellIndex; j++) {
      const vm = m[i][j];
      if (!vm.isSpan) {
        vm.cellAttr = _.merge(vm.cellAttr, cellAttr);
        vm.cellStyle = _.merge(vm.cellStyle, cellStyle);
        // 处理选中区域上边界
        /*border = borderPosition.t["border-top"];
        if (i === minRowIndex && border) {
          vm.cellBorderStyle["border-top"] = border;
        }
        // 处理选中区域左边界
        border = borderPosition.l["border-left"];
        if (j === minCellIndex && border) {
          vm.cellBorderStyle["border-left"] = border;
        }
        // 处理选中区域右边界
        border = borderPosition.r["border-right"];
        if (j + vm.colspan - 1 === maxCellIndex && border) {
          vm.cellBorderStyle["border-right"] = border;
        }
        // 处理选中区域下边界
        border = borderPosition.b["border-bottom"];
        if (i + vm.rowspan - 1 === maxRowIndex && border) {
          vm.cellBorderStyle["border-bottom"] = border;
        }*/
        // 处理选中区域中间单元格
        // border = borderPosition.cb["border-bottom"];
        // if (border) {
        //   let w = parseInt(border.split(" ")[0]);
        //   let _w = parseInt(w / 2);
        //   if (i > minRowIndex) {
        //     vm.cellBorderStyle["border-top"] = border.replace(w + "px", _w + "px");
        //   }
        //   if (i + vm.rowspan - 1 < maxRowIndex) {
        //     if (_w < w / 2) {
        //       _w++;
        //     }
        //     vm.cellBorderStyle["border-bottom"] = border.replace(w + "px", _w + "px");
        //   }
        // }
        // border = borderPosition.cr["border-right"];
        // if (border) {
        //   let w = parseInt(border.split(" ")[0]);
        //   let _w = parseInt(w / 2);
        //   if (j > minCellIndex) {
        //     vm.cellBorderStyle["border-left"] = border.replace(w + "px", _w + "px");
        //   }
        //   if (j + vm.colspan - 1 < maxCellIndex) {
        //     if (_w < w / 2) {
        //       _w++;
        //     }
        //     vm.cellBorderStyle["border-right"] = border.replace(w + "px", _w + "px");
        //   }
        // }
      }
    }
  }
}

// 清除选中单元格格式
function clearChooseCell() {
  let m = parseMatrix.call(this);
  let { maxRowIndex, minRowIndex, maxCellIndex, minCellIndex } = getRangeSelectedRect.call(this);
  for (let i = minRowIndex; i <= maxRowIndex; i++) {
    for (let j = minCellIndex; j <= maxCellIndex; j++) {
      const vm = m[i][j];
      if (!vm.isSpan) {
        this.$set(vm, 'cellAttr', {
          align: "",
          valign: ""
        });
        this.$set(vm, 'cellStyle', {});
        this.$set(vm, 'cellBorderStyle', {});
        // vm.cellBorderStyle = {};
      }
    }
  }
}

// 清除所有单元格格式
function clearAllCell() {
  let m = parseMatrix.call(this);
  for (let i = 0; i < m.length; i++) {
    for (let j = 0; j < m[i].length; j++) {
      const vm = m[i][j];
      if (!vm.isSpan) {
        this.$set(vm, 'cellAttr', {
          align: "",
          valign: ""
        });
        this.$set(vm, 'cellStyle', {});
        this.$set(vm, 'cellBorderStyle', {});
      }
    }
  }
}

function getCellContextMenuConfig(setCellStyle, setCellBorder) {
  return [{
    text: "字体", children: _.map([
      ['跟随上下文', ''],
      ['仿宋', '仿宋,FangSong'],
      ['黑体', '黑体, SimHei'],
      ['楷体', '楷体,楷体_GB2312, SimKai'],
      ['隶书', '隶书,SimLi'],
      ['宋体', '宋体,SimSun'],
      ['微软雅黑', '微软雅黑,Microsoft YaHei'],
      ['幼圆', '幼圆,YouYuan'],
      ['andale mono', 'andale mono'],
      ['arial', 'arial, helvetica,sans-serif'],
      ['arial black', 'arial black,avant garde'],
      ['comic sans ms', 'comic sans ms'],
      ['impact', 'impact,chicago'],
      ['times new roman', 'times new roman']
    ], ([text, value]) => {
      return { text, value, attr: 'font-family', handler: setCellStyle };
    })}, {
    text: "字体大小", children: _.map([
      ['跟随上下文', ''],
      ['10px', '10px'],
      ['11px', '11px'],
      ['12px', '12px'],
      ['14px', '14px'],
      ['16px', '16px'],
      ['18px', '18px'],
      ['20px', '20px'],
      ['22px', '22px'],
      ['24px', '24px'],
      ['36px', '36px'],
    ], ([text, value]) => {
      return { text, value, attr: 'font-size', handler: setCellStyle };
    })}, {
    text: "文本装饰", children: _.map([
      ['跟随上下文', ''],
      ['无装饰', 'none'],
      ['下划线', 'underline'],
      ['上划线', 'overline'],
      ['贯穿线', 'line-through']
    ], ([text, value]) => {
      return { text, value, attr: 'text-decoration', handler: setCellStyle };
    })}, {
    text: "文本加粗", children: _.map([
      ['跟随上下文', ''],
      ['正常', 'normal'],
      ['加粗', 'bold']
    ], ([text, value]) => {
      return { text, value, attr: 'font-weight', handler: setCellStyle };
    })}, {
    text: "文本斜体", children: _.map([
      ['跟随上下文', ''],
      ['正常', 'normal'],
      ['斜体', 'italic']
    ], ([text, value]) => {
      return { text, value, attr: 'font-style', handler: setCellStyle };
    })}, {
    text: "字体颜色", children: _.map([
      '', '#ffffff', '#000000', '#ff4500', '#ff8c00', '#ffd700', '#90ee90', '#00ced1', '#1e90ff', '#c71585', 'custom'
    ], (color) => {
      if (color === '') {
        return { text: '跟随上下文', value: '', attr: 'color', handler: setCellStyle };
      }
      if (color === 'custom') {
        return { text: '自定义', value: 'custom', attr: 'color', icon: 'icon-zidingyi', handler: setCellStyle };
      }
      return { color, text: color, value: color, icon: 'icon-ditu-yuan', attr: 'color', handler: setCellStyle };
    })}, {
    text: "背景填充", children: _.map([
      '', '#ffffff', '#000000', '#ff4500', '#ff8c00', '#ffd700', '#90ee90', '#00ced1', '#1e90ff', '#c71585', 'custom'
    ], (color) => {
      if (color === '') {
        return { text: '跟随上下文', value: '', attr: 'background-color', handler: setCellStyle };
      }
      if (color === 'custom') {
        return { text: '自定义', value: 'custom', attr: 'background-color', icon: 'icon-zidingyi', handler: setCellStyle };
      }
      return { color, text: color, value: color, icon: 'icon-ditu-yuan', attr: 'background-color', handler: setCellStyle };
    })}, {
    text: "水平对齐", children: _.map([
      ['跟随上下文', ''],
      ['靠左', 'left'],
      ['居中', 'center'],
      ['靠右', 'right']
    ], ([text, value]) => {
      return { text, value, attr: 'align', handler: setCellStyle };
    })}, {
    text: "垂直对齐", children: _.map([
      ['跟随上下文', ''],
      ['靠上', 'top'],
      ['居中', 'middle'],
      ['靠下', 'bottom'],
      ['基线对齐', 'baseline']
    ], ([text, value]) => {
      return { text, value, attr: 'valign', handler: setCellStyle };
    })}, {
    text: "边框样式", children: _.map([{
      text: "全部边框", children: [{
        text: "跟随上下文", value: '', attr: 'all', handler: setCellBorder}, {
        text: "隐藏", value: 'none', attr: 'all,none', handler: setCellBorder}, {
        text: "颜色", children: _.map([
          '', '#ffffff', '#000000', '#ff4500', '#ff8c00', '#ffd700', '#90ee90', '#00ced1', '#1e90ff', '#c71585', 'custom'
        ], (color) => {
          if (color === '') {
            return { text: '跟随上下文', value: '', attr: 'all,color', handler: setCellBorder };
          }
          if (color === 'custom') {
            return { text: '自定义', attr: 'all,color', value: 'custom', icon: 'icon-zidingyi', handler: setCellBorder };
          }
          return { color, text: color, value: color, icon: 'icon-ditu-yuan', attr: 'all,color', handler: setCellBorder };
        })
      }]}, {
      text: "上边框", children: [{
        text: "跟随上下文", value: '', attr: 'top', handler: setCellBorder}, {
        text: "隐藏", value: 'none', attr: 'top,none', handler: setCellBorder}, {
        text: "颜色", children: _.map([
          '', '#ffffff', '#000000', '#ff4500', '#ff8c00', '#ffd700', '#90ee90', '#00ced1', '#1e90ff', '#c71585', 'custom'
        ], (color) => {
          if (color === '') {
            return { text: '跟随上下文', value: '', attr: 'top,color', handler: setCellBorder };
          }
          if (color === 'custom') {
            return { text: '自定义', attr: 'top,color', value: 'custom', icon: 'icon-zidingyi', handler: setCellBorder };
          }
          return { color, text: color, value: color, icon: 'icon-ditu-yuan', attr: 'top,color', handler: setCellBorder };
        })
      }]}, {
      text: "下边框", children: [{
        text: "跟随上下文", value: '', attr: 'bottom', handler: setCellBorder}, {
        text: "隐藏", value: 'none', attr: 'bottom,none', handler: setCellBorder}, {
        text: "颜色", children: _.map([
          '', '#ffffff', '#000000', '#ff4500', '#ff8c00', '#ffd700', '#90ee90', '#00ced1', '#1e90ff', '#c71585', 'custom'
        ], (color) => {
          if (color === '') {
            return { text: '跟随上下文', value: '', attr: 'bottom,color', handler: setCellBorder };
          }
          if (color === 'custom') {
            return { text: '自定义', attr: 'bottom,color', value: 'custom', icon: 'icon-zidingyi', handler: setCellBorder };
          }
          return { color, text: color, value: color, icon: 'icon-ditu-yuan', attr: 'bottom,color', handler: setCellBorder };
        })
      }]}, {
      text: "左边框", children: [{
        text: "跟随上下文", value: '', attr: 'left', handler: setCellBorder}, {
        text: "隐藏", value: 'none', attr: 'left,none', handler: setCellBorder}, {
        text: "颜色", children: _.map([
          '', '#ffffff', '#000000', '#ff4500', '#ff8c00', '#ffd700', '#90ee90', '#00ced1', '#1e90ff', '#c71585', 'custom'
        ], (color) => {
          if (color === '') {
            return { text: '跟随上下文', value: '', attr: 'left,color', handler: setCellBorder };
          }
          if (color === 'custom') {
            return { text: '自定义', attr: 'left,color', value: 'custom', icon: 'icon-zidingyi', handler: setCellBorder };
          }
          return { color, text: color, value: color, icon: 'icon-ditu-yuan', attr: 'left,color', handler: setCellBorder };
        })
      }]}, {
      text: "右边框", children: [{
        text: "跟随上下文", value: '', attr: 'right', handler: setCellBorder}, {
        text: "隐藏", value: 'none', attr: 'right,none', handler: setCellBorder}, {
        text: "颜色", children: _.map([
          '', '#ffffff', '#000000', '#ff4500', '#ff8c00', '#ffd700', '#90ee90', '#00ced1', '#1e90ff', '#c71585', 'custom'
        ], (color) => {
          if (color === '') {
            return { text: '跟随上下文', value: '', attr: 'right,color', handler: setCellBorder };
          }
          if (color === 'custom') {
            return { text: '自定义', attr: 'right,color', value: 'custom', icon: 'icon-zidingyi', handler: setCellBorder };
          }
          return { color, text: color, value: color, icon: 'icon-ditu-yuan', attr: 'right,color', handler: setCellBorder };
        })
      }]}
    ])}
  ];
}
</script>
